/**
 * @file        src/pages/hotel/DiscountList.jsx
 * @description B2C Hotel Discount List (`hotel_discounts`). Legacy parity
 *              columns: # / Amount / Updated On / Action (Delete · Edit).
 *              Add + edit live on the AddDiscount form page.
 * @author      Kinetika Labs
 * @created     2026-07-02
 * @changelog
 *   - 2026-07-02 Claude — Initial (B2C Hotel Management).
 *   - 2026-07-12 Claude — Legacy parity columns (# / Amount / Updated On /
 *                          Action) + "Add B2C Discount".
 *   - 2026-08-03 Claude — Brand column (registry name for the row's 3-char code).
 *   - 2026-08-10 Claude — Added title/subtitle/audience props (defaulting to the
 *                          unchanged B2C behaviour) so CorporateDiscountList.jsx
 *                          can reuse this page fixed to audience=b2b instead of
 *                          forking it — and an "Applies To" column so the new
 *                          `applicable_role` field (All/B2C/B2B) is visible.
 *   - 2026-09-12 Claude — "+ Add Discount" now carries `?audience=b2b` from the
 *                          Corporate list into the shared AddDiscount form,
 *                          which stopped asking for "Applies To". Without it the
 *                          form defaulted every new row to 'all', so a corporate
 *                          discount also applied to retail.
 *   - 2026-09-15 Claude — Added the one-line note stating HOW a discount reaches
 *                          the bill: automatically, best-value rule only, off the
 *                          ROOM TOTAL — the convenience fee is added after and is
 *                          never discounted, which is the opposite of flights and
 *                          is not inferable from this list.
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 *   - 2026-10-06 Claude — "Dom / Int" column (migration 076).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import { HOTEL_DISCOUNT_NOTE } from '../../lib/pricingNotes';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { useBrands } from '../../api/brands';
import { get, del } from '../../api/client';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';
import { money, dateTime } from './cells';
import { domIntLabel } from '../../lib/domInt';

const ROLE_LABEL = { all: 'All (legacy)', b2c: 'B2C', b2b: 'B2B' };

export default function DiscountList({
  title = 'B2C Hotel Discount List',
  subtitle = 'B2C Hotel Management',
  audience = 'b2c',
} = {}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { labelOf } = useBrands();
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const params = { page, pageSize, audience };
  const { data, isLoading } = useQuery({
    queryKey: ['hotel-discounts', 'list', params],
    queryFn: () => get('/hotel-discounts', { params }),
    placeholderData: keepPreviousData,
  });
  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  const removeMut = useMutation({
    mutationFn: (id) => del(`/hotel-discounts/${id}`),
    onSuccess: () => {
      toast.ok('Deleted', 'Discount removed.');
      qc.invalidateQueries({ queryKey: ['hotel-discounts'] });
    },
    onError: (err) => toast.err('Failed', err.response?.data?.error?.message || 'Could not delete'),
  });

  async function onDelete(row) {
    if (!(await confirm(`Delete discount #${row.id}?`))) return;
    removeMut.mutate(row.id);
  }

  // "Amount" carries both kinds of rule — 5% for a percentage discount, ₹500 for
  // a flat one — so the legacy single-column layout still tells the whole story.
  const amount = (r) => (r.discount_type === 'percentage' ? `${r.discount_value}%` : money(r.discount_value));

  return (
    <>
      <PageHead
        title={title}
        subtitle={subtitle}
        actions={(
          // Carry THIS list's audience to the shared Add form, which no longer
          // asks — otherwise a corporate discount is created as 'all' and prices
          // retail bookings too.
          <button className="btn primary" onClick={() => navigate(`/hotel/discounts/new${audience === 'b2b' ? '?audience=b2b' : ''}`)}>
            <Icon name="plus" /> Add Discount
          </button>
        )}
      />

      <div className="notice-banner info">{HOTEL_DISCOUNT_NOTE}</div>

      <article className="panel glass">
        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr><th>#</th><th>Brand</th><th>Amount</th><th>Dom / Int</th><th>Applies To</th><th>Updated On</th><th>Action</th></tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={7} className="empty">No hotel discounts yet. Add your first one.</td></tr>
                ) : rows.map((r, idx) => (
                  <tr key={r.id}>
                    <td className="mono">{(page - 1) * pageSize + idx + 1}</td>
                    <td>{labelOf(r.brand)}</td>
                    <td>
                      <b>{amount(r)}</b>
                      {!r.is_active ? <> <Chip tone="rej">Inactive</Chip></> : null}
                      {r.name ? <div className="muted" style={{ fontSize: '.8em' }}>{r.name}</div> : null}
                    </td>
                    <td>{domIntLabel(r.dom_int)}</td>
                    <td>{ROLE_LABEL[r.applicable_role] || '—'}</td>
                    <td className="muted">{dateTime(r.updated_at)}</td>
                    <td className="row-act">
                      <button className="ib del" aria-label="Delete" onClick={() => onDelete(r)}><Icon name="trash" /></button>
                      <button className="ib edit" aria-label="Edit" onClick={() => navigate(`/hotel/discounts/${r.id}/edit`)}><Icon name="edit" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>
    </>
  );
}
